/**
 * Orders as the home screen and history need them.
 *
 * The one thing to get right here: `GET /v1/consumer/orders/active` returns an
 * **array of 0..N**, because an active order belongs to a CAR, not to the account —
 * a rider with three cars can have three выезда running at once. Code that reads
 * `[0]` and calls it "the active order" is wrong, and both mobile clients are built
 * that way today (docs/27 §6, wave 2).
 */
import { apiRequest } from '@/shared/api/client';

export interface ConsumerOrderSummary {
  id: string;
  state: string;
  tracking_substate: string;
  /** The FIRST car of the выезд — all a card needs to render without the whole list. */
  car_brand: string;
  car_model: string;
  car_color: string;
  /**
   * How many cars this выезд still covers; 1 for an ordinary order. Counts only cars
   * still ON the trip, so «Camry +1» never lingers after a car was dropped.
   */
  car_count: number;
  /**
   * Which of the consumer's cars this выезд is holding right now.
   *
   * Only cars still on the trip: a dropped one holds nothing and can be ordered for again
   * immediately. Optional because a backend that predates the field simply omits it, and the
   * picker then falls back to offering everything and letting the server refuse.
   */
  car_ids?: string[];
  created_at: string;
}

export function fetchActiveOrders(): Promise<ConsumerOrderSummary[]> {
  return apiRequest<ConsumerOrderSummary[]>('/v1/consumer/orders/active');
}

/** One finished order as the list endpoint sends it. Thinner than the detail on purpose. */
export interface ConsumerOrderHistoryRow {
  id: string;
  state: string;
  car_brand: string;
  car_model: string;
  car_color: string;
  car_count: number;
  total_price: string | null;
  fuel_amount_liters: string | null;
  cancellation_fee_amount: string;
  charged_on_cancellation: boolean;
  created_at: string;
}

export interface ConsumerOrderHistoryPage {
  orders: ConsumerOrderHistoryRow[];
  total: number;
  limit: number;
  offset: number;
}

export const HISTORY_PAGE_SIZE = 20;

/**
 * Finished orders, newest first.
 *
 * `status` filters server-side; the client never re-derives «завершён» or «отменён» from a state
 * string, for the same reason it does not re-derive the row's own face.
 */
export function fetchOrderHistory(
  limit: number = HISTORY_PAGE_SIZE,
  offset = 0,
  status?: string,
): Promise<ConsumerOrderHistoryPage> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (status !== undefined) params.set('status', status);
  return apiRequest<ConsumerOrderHistoryPage>(`/v1/consumer/orders?${params.toString()}`);
}
